import { EmptyView } from "@eusoft/webapp-framework/components/EmptyView";
import { FieldSearchMode, FilterEditor } from "@eusoft/webapp-framework/components/FilterEditor";
import { buildContent } from "@eusoft/webapp-framework/content/Builder";
import { ItemListContent } from "@eusoft/webapp-framework/content/ItemListContent";
import { numberRangeSearch } from "@eusoft/webapp-framework/helpers/SmartSearch";
import { enumItemsSource, formatCurrency, formatString, formatText } from "@eusoft/webapp-ui";
import { MaterialIcon } from "@eusoft/webapp-ui/components/Icon";
import { navigatePage } from "../../actions/Common";
import { blockCardAsync, replaceCardAsync, unblockCardAsync } from "../../actions/PaymentCard";
import { PaymentCardStatus, PaymentCardType, SalePointType, SortDirection, type ICardListView, type IListCard, type IListSalePoint } from "../../entities/Commands";
import { Icons } from "../../entities/Icons";
import { formatCardStatus } from "../../helpers/Format";
import { cardsItemsSource, entityItemsSource } from "../../helpers/ItemsSources";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { WithContext } from "../../services/WithContext";
import { paymentService } from "../../abstraction/IPaymentService";
import { executeAsync } from "../../services/DataService";


const createFilter = () => new FilterEditor<IListCard, ICardListView>({
    fields: {
        emittedSalePointId: {
            label: "sale-point",
            valuesSource: entityItemsSource("ListSalePoint"),
            searchMode: FieldSearchMode.Client,
            valuesFilter: (filter, query) => ({
                saleCircuitId: context.saleCircuit?.id,
                type: [SalePointType.Charge],
            } as IListSalePoint),
            icon: Icons["sale-point"],
            color: "#2196F3", // blue 500
        },
        ids: {
            label: "card",
            multipleValues: true,
            searchMode: FieldSearchMode.LabelOnly,
            icon: Icons["payment-card"],
            minQueryLen: 1,
            pickAsync: async () => {
                const card = await paymentService.readCardAsync({
                    message: formatString("msg-wait-for-card-read")
                });

                if (card?.code) {
                    const info = await executeAsync("GetCardInfo", { cardCode: card.code });
                    if (!info?.cardId)
                        return;
                    return {
                        text: card.code,
                        value: info.cardId
                    };
                }
            },
            color: "#009688", // teal 500
            valuesFilter: (filter, query) => ({
                code: query.full,
                saleCircuitId: context.saleCircuit?.id
            } as Partial<IListCard>)
        },
        status: {
            multipleValues: false,
            dataType: "enum",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(PaymentCardStatus, "sps"),
            icon: <MaterialIcon name="chronic" />,
            color: "#FF5722", // deep-orange 500
        },
        type: {
            multipleValues: false,
            dataType: "enum",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(PaymentCardType),
            icon: <MaterialIcon name="category" />,
            color: "#3F51B5", // indigo 500
        }
    },
    searchProviders: [numberRangeSearch({
        maxField: "balanceMax",
        minField: "balanceMin",
        label: "balance",        
        format: formatCurrency,
        icon: <MaterialIcon name="attach_money" />,
        color: "#4CAF50", // green 500
        staticItems: [{
            min: 0.01,
            name: "with-balance"
        },
        {
            max: 0,
            name: "without-balance"
        }]
    })],
    renderMode: "field",
    matchFields: a => [a.code, a.customer?.name, a.customer?.email, a.customer?.mobileNumber],
    queryField: "query",
});



export const ListCardPage = buildContent(ItemListContent<ICardListView, IListCard>)

    .feature(Authorize(a => a
        .businessAccount()
        .resource("PaymentCard", "list")
    ))

    .feature(WithContext("sale-circuit"))

    .info({
        name: "card-list",
        route: "/cards/manage",
        icon: Icons["payment-card"],
    })

    .options({
        title: "card-list",
        style: ["panel", "round-item"],

        itemsSource: {
            ...entityItemsSource("ListCard"),
            getIcon: item => {
                return formatCardStatus(item.status).icon
            }
        },
        filterEditor: createFilter,
        prepareFilter: (filter, offset, limit) => ({
            ...filter,
            saleCircuitId: context.saleCircuit.id,
            pagination: {
                offset,
                limit
            },
            sort: {
                property: "LastOperationTime",
                direction: SortDirection.Descending
            }
        }),

        openItem: item => {
            navigatePage("card-details", {cardCode: item.code})
        },
        canOpen: true, 
        pageSize: 50,        
        itemView: item => ({
            maxActions: 2,
            style: ["round-icon", formatCardStatus(item.status).style],
        }), 
        itemActions: (item, result) => {

            if (item.status != PaymentCardStatus.Blocked) {
                result.push({
                    name: "block-card",
                    icon: <MaterialIcon name="credit_card_off" style="red" />,
                    text: "block-card",
                    priority: "secondary",
                    executeAsync: ctx => blockCardAsync(ctx.target),
                })
            }
            else if (item.status == PaymentCardStatus.Blocked) {
                result.push({
                    name: "unblock-card",
                    icon: <MaterialIcon name="credit_card" style="green" />,
                    text: "unblock-card",
                    priority: "secondary",
                    executeAsync: ctx => unblockCardAsync(ctx.target),
                });

                result.push({
                    name: "replace-card",
                    icon: <MaterialIcon name="change_circle" style="purple" />,
                    text: "replace-card",
                    priority: "secondary",
                    executeAsync: ctx => replaceCardAsync(ctx.target),
                })
            }
        },
        emptyView: <EmptyView
            iconName="credit_card"
            message="msg-no-cards"
        />,
        columns: [{
            value: a => a.code,
            priority: "primary",
            header: "name",
        }, {
            value: a => formatCurrency(a.balance),
            priority: "secondary",
            header: "balance",
        }, {
            value: a => a.customer?.name ?? formatText("anonymous"),
            priority: "secondary",
            header: "customer",
        }],
        filterMode: "tags"

    }).asContent();